import {
  Chart,
  CategoryScale,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Filler
} from 'chart.js';
import { useRef, useEffect } from 'react';
import {
  formatShortDate,
  durationToString,
  msToHours,
  hoursToMs,
  median,
  average as avg,
} from '#utils';

Chart.register(
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  LineController,
  PointElement,
  Tooltip
);

const defaultFontColor = '#CCC';

const datasetConfig = {
  pointHoverRadius: 5,
  pointHitRadius: 10,
  borderWidth: 2,
  lineTension: 0,
};

Chart.defaults.color = defaultFontColor;

const getReviewsDataset = (reviews) => ({
  ...datasetConfig,
  data: reviews.map((r) => msToHours(r.time)), // Passing to hours to force Chart.js pick better ticks for Y axis
  label: 'Time to review',
  order: 0,
  borderColor: 'rgba(0, 172, 193, 1)',
  backgroundColor: 'rgba(0, 172, 193, 0.2)',
  pointRadius: 2,
  fill: true,
});

const getMedianDataset = (median, length) => ({
  ...datasetConfig,
  data: [...Array(length)].map((_) => msToHours(median)),
  label: 'Median',
  order: 1,
  borderColor: 'rgba(252, 170, 15, 1)',
  pointRadius: 0,
  fill: false,
});

const getAverageDataset = (average, length) => ({
  ...datasetConfig,
  data: [...Array(length)].map((_) => msToHours(average)),
  label: 'Average',
  order: 2,
  borderColor: 'rgba(233, 30, 99, 1)',
  pointRadius: 0,
  fill: false,
  hidden: true,
});

const getYAxisOptions = () => ({
  type: 'linear',
  position: 'left',
  min: 0,
  ticks: {
    callback: (value) => durationToString(hoursToMs(value)),
  },
  grid: {
    color: 'rgba(128, 128, 128, 0.1)',
    lineWidth: 1,
  },
});

const getXAxisOptions = () => ({
  type: 'category',
  position: 'left',
  ticks: {
    minRotation: 45,
    autoSkipPadding: 10,
  },
  grid: {
    display: false,
  },
});

const getTooltipOptions = () => ({
  mode: 'index',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  intersect: false,
  titleSpacing: 6,
  padding: 8,
  callbacks: {
    label: (item) => {
      const { label } = item.dataset;
      const { y } = item.parsed;
      return `${label}: ${durationToString(hoursToMs(y))}`
    },
    labelColor: (item) => {
      const { borderColor } = item.dataset;
      return { borderColor, backgroundColor: borderColor };
    }
  }
});

const getChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: 16,
  },
  hover: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    y: getYAxisOptions(),
    x: getXAxisOptions(),
  },
  plugins: {
    tooltip: getTooltipOptions(),
  }
});

const getChartData = (reviews) => {
  const labels = reviews.map((r) => formatShortDate(r.date));
  const medianHrs = median(reviews.map((r) => r.time));
  const averageHrs = avg(reviews.map((r) => r.time));

  return {
    labels,
    datasets: [
      getReviewsDataset(reviews),
      getMedianDataset(medianHrs, labels.length),
      getAverageDataset(averageHrs, labels.length),
    ],
  };
};

export const useReviewTimeChart = (reviews) => {
  const chartRef = useRef();
  const chartInstanceRef = useRef();

  useEffect(() => {
    if(!chartRef.current) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    chartInstanceRef.current = new Chart(chartRef.current, {
      type: 'line',
      data: getChartData(reviews),
      options: getChartOptions(),
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    }
  }, [chartRef.current, reviews]);

  return { chartRef };
};